/**
 * Cash ↔ Payments Mismatch Console (Feb 2026).
 *
 * Read-only diagnostic viewer + opt-in bulk heal for the
 * ``Cash ↔ Payments`` variance flagged by the Financial Truth
 * Reconciliation card. Backed by:
 *
 *   GET  /api/diagnostics/cash-gl-mismatch/{period}
 *   POST /api/diagnostics/cash-gl-mismatch/{period}/heal?dry_run=<bool>
 *
 * UX principles:
 *   • The report renders side-by-side aggregates so a CFO can see the
 *     ledger gap at a glance BEFORE scrolling into per-row detail.
 *   • Heal is intentionally two-step (Dry Run → Confirm Post) so no
 *     journals ever land accidentally.
 *   • Only rows classified as ``no_journal`` / ``journal_missing`` are
 *     auto-fixable; the rest carry an explicit "manual review"
 *     badge — clicking them opens the underlying payment page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ExternalLink, FileText,
  Loader2, RefreshCcw, ShieldCheck, Wrench, XCircle,
} from 'lucide-react';

import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';


const REASON_LABEL = {
  no_journal:       { label: 'قيد مفقود كلياً',        tone: 'rose'   },
  journal_missing:  { label: 'journal_id مكسور',       tone: 'rose'   },
  journal_no_cash:  { label: 'قيد بدون سطر Cash',      tone: 'amber'  },
  amount_mismatch:  { label: 'المبالغ لا تتطابق',      tone: 'amber'  },
};

const fmtSAR = (n) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(n || 0)) + ' ﷼';


function StatBlock({ label, value, tone = 'slate', hint }) {
  const toneMap = {
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose:    'bg-rose-50 border-rose-200 text-rose-900',
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
  };
  return (
    <div className={`rounded-lg border ${toneMap[tone]} p-4`} data-testid={`stat-${label}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-xs opacity-70 mt-1">{hint}</div>}
    </div>
  );
}


export default function CashGLMismatchConsole() {
  const [period, setPeriod] = React.useState(
    new Date().toISOString().slice(0, 7));
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [healBusy, setHealBusy] = React.useState(false);
  const [healResult, setHealResult] = React.useState(null);

  // ── Part B — Broken Cash Accounts ────────────────────────────────
  const [broken, setBroken] = React.useState(null);
  const [brokenBusy, setBrokenBusy] = React.useState(false);
  const [applyBusy, setApplyBusy] = React.useState(null); // account_id in-flight
  const loadBroken = React.useCallback(async () => {
    setBrokenBusy(true);
    try {
      const { data } = await axiosInstance.get(
        '/diagnostics/broken-cash-accounts');
      setBroken(data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed to load broken cash accounts');
      setBroken(null);
    } finally {
      setBrokenBusy(false);
    }
  }, []);
  React.useEffect(() => { loadBroken(); }, [loadBroken]);
  const applyBrokenAction = async (row, action) => {
    if (!window.confirm(
      `تطبيق "${action}" على الحساب ${row.account_number} (${row.account_name})؟`
      + `\nApply ${action}?`)) return;
    setApplyBusy(row.account_id);
    try {
      await axiosInstance.post(
        `/diagnostics/broken-cash-accounts/${row.account_id}/apply`,
        { action });
      toast.success(`تم: ${action}`);
      loadBroken();
      load();       // reconciliation may now change too
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed');
    } finally {
      setApplyBusy(null);
    }
  };

  const load = React.useCallback(async () => {
    setLoading(true); setHealResult(null);
    try {
      const { data } = await axiosInstance.get(
        `/diagnostics/cash-gl-mismatch/${period}`);
      setReport(data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  React.useEffect(() => { load(); }, [load]);

  const runHeal = async (dryRun) => {
    if (!dryRun) {
      const auto = (report?.orphan_payments || [])
        .filter(o => ['no_journal', 'journal_missing'].includes(o.reason)).length;
      if (!auto) {
        toast.info('لا يوجد ما يمكن إصلاحه تلقائياً في هذه الفترة');
        return;
      }
      if (!window.confirm(
        `سيتم إنشاء ${auto} قيد يومية جديد لدفعات لا تحتوي قيوداً.\n`
        + `Continue with ${auto} auto-journal(s)?`)) return;
    }
    setHealBusy(true);
    try {
      const { data } = await axiosInstance.post(
        `/diagnostics/cash-gl-mismatch/${period}/heal`
        + (dryRun ? '?dry_run=true' : ''));
      setHealResult(data);
      const c = data.counts;
      toast.success(
        `${dryRun ? 'خطة الإصلاح' : 'تم الإصلاح'}: ` +
        `posted=${c.posted} · skipped=${c.skipped} · errored=${c.errored}`);
      if (!dryRun) load();     // refresh report
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : 'Heal failed');
    } finally {
      setHealBusy(false);
    }
  };

  // ── header ────────────────────────────────────────────────────────
  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <Link to="/reconciliation-center"
              className="text-slate-500 hover:text-slate-700"
              data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900">
          Cash ↔ Payments Diagnostic
        </h1>
        <Badge variant="outline" className="border-slate-300 text-slate-600">
          read-only + heal
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          placeholder="2026-06 أو 2026-Q2"
          className="w-40 h-9 font-mono text-sm"
          data-testid="period-input"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="refresh-btn"
        >
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
            : <RefreshCcw className="w-4 h-4 mr-1" />}
          تحديث
        </Button>
      </div>
    </div>
  );

  if (loading && !report) {
    return (
      <div className="p-6">
        {header}
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          جاري التحميل …
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6">
        {header}
        <Card><CardContent className="py-8 text-center text-slate-500">
          لم يتم تحميل التقرير — تحقق من الفترة وأعد المحاولة.
        </CardContent></Card>
      </div>
    );
  }

  const variance = report.variance_from_payment_journals;
  const hasVariance = Math.abs(variance) > 0.005;
  const orphans   = report.orphan_payments || [];
  const unmatched = report.unmatched_cash_lines || [];
  const autoFixable = orphans.filter(
    o => ['no_journal', 'journal_missing'].includes(o.reason)).length;

  // ── content ───────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="cash-gl-console">
      {header}

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatBlock
          label="Payments Net (Sub-Ledger)"
          value={fmtSAR(report.payments_net_expected)}
          hint={`Rcv ${fmtSAR(report.payments_total_received)} · Made ${fmtSAR(report.payments_total_made)}`}
          tone="indigo"
        />
        <StatBlock
          label="GL Cash from Payment Journals"
          value={fmtSAR(report.gl_cash_net_from_payments)}
          hint={`Cash Registry size: ${report.cash_registry_size}`}
          tone="slate"
        />
        <StatBlock
          label="Variance"
          value={fmtSAR(variance)}
          hint={hasVariance ? 'يوجد اختلاف' : 'متطابقان تماماً'}
          tone={hasVariance ? 'rose' : 'emerald'}
        />
        <StatBlock
          label="Orphan Payments"
          value={`${orphans.length}`}
          hint={`إجمالي الفروق: ${fmtSAR(report.orphan_payments_variance)}`}
          tone={orphans.length ? 'rose' : 'emerald'}
        />
      </div>

      {/* Heal actions */}
      {orphans.length > 0 && (
        <Card className="mb-6 border-amber-300 bg-amber-50/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4 text-amber-700" />
              إصلاح تلقائي — {autoFixable} دفعة قابلة للإصلاح
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
            <span>سيتم إنشاء قيود يومية جديدة (Dr Cash / Cr AP/AR) للدفعات ذات الحالة <code>no_journal</code> أو <code>journal_missing</code>. الباقي يحتاج مراجعة يدوية.</span>
            <div className="ml-auto flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => runHeal(true)}
                disabled={healBusy}
                data-testid="dry-run-btn"
                className="border-amber-400 text-amber-800 hover:bg-amber-100"
              >
                {healBusy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                محاكاة (Dry Run)
              </Button>
              <Button
                size="sm"
                onClick={() => runHeal(false)}
                disabled={healBusy || autoFixable === 0}
                data-testid="run-heal-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {healBusy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  : <Wrench className="w-3.5 h-3.5 mr-1" />}
                تنفيذ الإصلاح ({autoFixable})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Heal result panel */}
      {healResult && (
        <Card className="mb-6 border-slate-300">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-700" />
              نتيجة {healResult.counts.dry_run ? 'المحاكاة' : 'الإصلاح'}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3 text-sm">
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-xs uppercase tracking-wide text-emerald-700">Posted</div>
              <div className="text-2xl font-semibold text-emerald-900">
                {healResult.counts.posted}
              </div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-600">Skipped</div>
              <div className="text-2xl font-semibold text-slate-900">
                {healResult.counts.skipped}
              </div>
            </div>
            <div className="rounded border border-rose-200 bg-rose-50 p-3">
              <div className="text-xs uppercase tracking-wide text-rose-700">Errored</div>
              <div className="text-2xl font-semibold text-rose-900">
                {healResult.counts.errored}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orphan Payments table */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            الدفعات اليتيمة ({orphans.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orphans.length === 0 ? (
            <div className="flex items-center gap-2 text-emerald-700 py-4">
              <CheckCircle2 className="w-4 h-4" />
              لا يوجد دفعات يتيمة — كل الدفعات في هذه الفترة لها قيود مطابقة.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">In Journal</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Counterparty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orphans.map((o, i) => {
                    const rl = REASON_LABEL[o.reason]
                              || { label: o.reason, tone: 'slate' };
                    return (
                      <TableRow key={o.id} data-testid={`orphan-${o.payment_no}`}>
                        <TableCell className="text-slate-500">{i + 1}</TableCell>
                        <TableCell className="font-mono text-xs">
                          <Link to={`/payments?highlight=${o.id}`}
                                className="text-indigo-700 hover:underline inline-flex items-center gap-1">
                            {o.payment_no || o.id.slice(0, 8)}
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </TableCell>
                        <TableCell className="text-slate-600 text-xs">
                          {o.payment_date}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {o.payment_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtSAR(o.amount)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-600">
                          {fmtSAR(o.cash_net_in_journal)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-rose-700">
                          {fmtSAR(o.variance)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            rl.tone === 'rose'
                              ? 'bg-rose-100 text-rose-800 border-rose-300'
                              : 'bg-amber-100 text-amber-800 border-amber-300'
                          }>
                            {rl.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-600 text-xs truncate max-w-[200px]">
                          {o.counterparty || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unmatched Cash Lines */}
      {unmatched.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <XCircle className="w-4 h-4 text-amber-600" />
              قيود Cash بلا دفعة مقابلة ({unmatched.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-slate-600 mb-2">
              حركة في حسابات Cash بدون سجل دفعة (deposit يدوي، تسوية، manual posting).
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Journal #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unmatched.map((u, i) => (
                    <TableRow key={`${u.journal_id}-${i}`}
                              data-testid={`unmatched-${u.journal_no}`}>
                      <TableCell className="font-mono text-xs">
                        {u.journal_no || u.journal_id.slice(0, 8)}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {u.journal_date}
                      </TableCell>
                      <TableCell className="text-xs">
                        {u.account_number} · {u.account_name}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtSAR(u.debit)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtSAR(u.credit)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {fmtSAR(u.net_signed)}
                      </TableCell>
                      <TableCell className="text-slate-600 text-xs max-w-[280px] truncate">
                        {u.description || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Part B — Broken Cash Accounts ─────────────────────────── */}
      <Card className="mt-6" data-testid="broken-cash-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-slate-700" />
            حسابات Cash معطوبة (مُصنَّفة كـ cash بلا رابط بنكي)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {brokenBusy && !broken ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> جاري التحميل…
            </div>
          ) : !broken ? (
            <div className="text-slate-500">لم يتم تحميل التقرير.</div>
          ) : broken.broken_count === 0 ? (
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              لا يوجد حسابات معطوبة — كل الحسابات المُصنَّفة كـ cash
              مربوطة بحسابات بنكية.
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-600 mb-3">
                {broken.bank_linked_count} مربوط ببنك · {broken.broken_count} معطوب.
                لكل حساب: اختر الإجراء المناسب حسب طبيعة الحساب في شركتك.
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>رقم</TableHead>
                      <TableHead>الاسم</TableHead>
                      <TableHead className="text-right">قيود 90 يوم</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead>آخر حركة</TableHead>
                      <TableHead>الاقتراح</TableHead>
                      <TableHead>الإجراء</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {broken.broken_accounts.map((r) => {
                      const busy = applyBusy === r.account_id;
                      return (
                        <TableRow key={r.account_id}
                                    data-testid={`broken-${r.account_number}`}>
                          <TableCell className="font-mono text-xs">
                            {r.account_number}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.account_name}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {r.journals_90d}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {fmtSAR(r.total_debit_90d)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {fmtSAR(r.total_credit_90d)}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {r.last_journal_date || '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {r.suggested_action}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm" variant="outline"
                                onClick={() => applyBrokenAction(r, 'keep_as_cash')}
                                disabled={busy}
                                data-testid={`keep-cash-${r.account_number}`}
                                className="h-7 text-xs"
                              >
                                keep
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                onClick={() => applyBrokenAction(r, 'reclassify_non_cash')}
                                disabled={busy}
                                data-testid={`reclassify-${r.account_number}`}
                                className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                              >
                                reclassify
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
